/* eslint-disable max-lines */
import store from 'store';
import { APP_ROLES } from 'store/enums';
import { GET_DRIVER, RESET_INTERVENTIONS, OFFER_LEGAL_CONDITIONS } from 'store/actions/vehicleInfo';
import { UI_GET_MASTER_DATA, UI_GET_DOCUMENTATION_MASTERDATA } from 'store/actions/ui';
import { userSignedIn, userDriverId } from 'helpers/user';

const Layout = () => import(/* webpackChunkName: "pages/main" */ 'layout');
const LayoutAccessDenied = () => import(/* webpackChunkName: "pages/main" */ 'layoutAccessDenied');
const Error = () => import(/* webpackChunkName: "pages/views/error" */ 'views/Error');
const Login = () => import(/* webpackChunkName: "pages/views/login" */ 'views/Login');
const Legal = () => import(/* webpackChunkName: "pages/views/legal" */ 'views/Legal');
const Dashboard = () =>
  import(/* webpackChunkName: "pages/views/App/Dashboard" */ 'views/App/Dashboard');
const Remember = () => import(/* webpackChunkName: "pages/views/Remember" */ 'views/Remember');
const VerifyPass = () =>
  import(/* webpackChunkName: "pages/views/Remember/VerifyPass" */ 'views/Remember/VerifyPass');
const NewUser = () => import(/* webpackChunkName: "pages/views/NewUser" */ 'views/NewUser');
const ConfirmData = () =>
  import(/* webpackChunkName: "pages/views/NewUser/ConfirmData" */ 'views/NewUser/ConfirmData');
const ManageAppointment = () =>
  import(
    /* webpackChunkName: "pages/views/App/Appointment/ManageAppointment" */ 'views/App/Appointment/ManageAppointment'
  );
const AppointmentAverages = () =>
  import(
    /* webpackChunkName: "pages/views/App/Appointment/Averages" */ 'views/App/Appointment/Averages'
  );
const AppointmentGlassWindows = () =>
  import(
    /* webpackChunkName: "pages/views/App/Appointment/GlassWindows" */ 'views/App/Appointment/GlassWindows'
  );
const AppointmentRevision = () =>
  import(
    /* webpackChunkName: "pages/views/App/Appointment/Revision" */ 'views/App/Appointment/Revision'
  );
const AppointmentTyres = () =>
  import(/* webpackChunkName: "pages/views/App/Appointment/Tyres" */ 'views/App/Appointment/Tyres');
const HistoryInterventions = () =>
  import(
    /* webpackChunkName: "pages/views/App/HistoryInterventions" */ 'views/App/HistoryInterventions'
  );
const ChangePassword = () =>
  import(
    /* webpackChunkName: "pages/views/App/MyAccount/ChangePassword" */ 'views/App/MyAccount/ChangePassword'
  );
const PersonalInformation = () =>
  import(
    /* webpackChunkName: "pages/views/App/MyAccount/PersonalInformation" */ 'views/App/MyAccount/PersonalInformation'
  );
const VehicleInformation = () =>
  import(
    /* webpackChunkName: "pages/views/App/MyAccount/VehicleInformation" */ 'views/App/MyAccount/VehicleInformation'
  );
const CardFuel = () =>
  import(
    /* webpackChunkName: "pages/views/App/OtherServices/CardFuel" */ 'views/App/OtherServices/CardFuel'
  );
const Documentation = () =>
  import(
    /* webpackChunkName: "pages/views/App/OtherServices/Documentation" */ 'views/App/OtherServices/Documentation'
  );
const RequestReplacementVehicle = () =>
  import(
    /* webpackChunkName: "pages/views/App/OtherServices/RequestReplacementVehicle" */ 'views/App/OtherServices/RequestReplacementVehicle'
  );
// const Fines = () =>
//   import(
//     /* webpackChunkName: "pages/views/App/OtherServices/Fines" */ 'views/App/OtherServices/Fines'
//   );
// const IncidentsDGT = () =>
//   import(
//     /* webpackChunkName: "pages/views/App/OtherServices/IncidentsDGT" */ 'views/App/OtherServices/IncidentsDGT'
//   );
// const PointsInterest = () =>
//   import(
//     /* webpackChunkName: "pages/views/App/OtherServices/PointsInterest" */ 'views/App/OtherServices/PointsInterest'
//   );
const RoadsideAssistance = () =>
  import(
    /* webpackChunkName: "pages/views/App/RoadsideAssistance" */ 'views/App/RoadsideAssistance'
  );
const AccidentManagement = () =>
  import(
    /* webpackChunkName: "pages/views/App/RoadsideAssistance/AccidentManagement" */ 'views/App/RoadsideAssistance/AccidentManagement'
  );
const ContactAssistance = () =>
  import(
    /* webpackChunkName: "pages/views/App/RoadsideAssistance/Contact" */ 'views/App/RoadsideAssistance/Contact'
  );
// const Notifications = () =>
//   import(/* webpackChunkName: "pages/views/App/Notifications" */ 'views/App/Notifications');
const ReturnCar = () =>
  import(
    /* webpackChunkName: "views/App/OtherServices/ReturnCar" */ 'views/App/OtherServices/ReturnCar'
  );
const OidcCallback = () =>
  import(
    /* webpackChunkName: "pages/oidc/callback" */
    'views/Login/OidcCallback'
  );
const PageAccessDenied = () =>
  import(
    /* webpackChunkName: "pages/views/errors/access-denied" */
    'views/Error/AccessDenied'
  );

// Redirect to the error page
const redirectToErrorPage = (next, resource) =>
  next({
    name: 'error',
    params: { resource }
  });

// Redirect to the 404 page
// eslint-disable-next-line no-unused-vars
const redirectTo404Page = (next, path) => next({ name: '404', params: { 0: path } });

export default [
  {
    path: '/oidc-callback',
    name: 'oidc-callback',
    component: OidcCallback,
    meta: { titleKey: 'oidcCallback' }
  },
  {
    path: '/accessdenied',
    component: LayoutAccessDenied,
    children: [
      {
        path: '',
        name: 'access-denied',
        component: PageAccessDenied,
        meta: {
          titleKey: 'accessDenied',
          requiresAuth: false
        }
      }
    ]
  },
  {
    path: '/',
    component: Layout,
    beforeEnter(to, from, next) {
      next();
    },
    children: [
      {
        path: '/login',
        component: Login,
        meta: { titleKey: 'login', requiresAuth: false },
        name: 'login',
        async beforeEnter(to, from, next) {
          const signedIn = userSignedIn();
          if (!signedIn) return next();
          next({
            name: 'dashboard',
            query: to.query
          });
        }
      },
      {
        path: '/dashboard',
        component: Dashboard,
        meta: {
          titleKey: 'dashboard',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'dashboard',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          Promise.all([
            store.dispatch(`vehicleInfo/${RESET_INTERVENTIONS}`),
            store.dispatch(`ui/${UI_GET_MASTER_DATA}`),
            store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId)
          ])
            .then(() => {
              next();
            })
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/appointment/manage-appointment',
        component: ManageAppointment,
        meta: {
          titleKey: 'manageAppointment',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'manageAppointment',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/appointment/averages',
        component: AppointmentAverages,
        meta: {
          titleKey: 'appointmentAverages',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'appointmentAverages',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/appointment/glass-windows',
        component: AppointmentGlassWindows,
        meta: {
          titleKey: 'appointmentGlassWindows',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'appointmentGlassWindows',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/appointment/revision',
        component: AppointmentRevision,
        meta: {
          titleKey: 'appointmentRevision',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'appointmentRevision',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/appointment/tyres',
        component: AppointmentTyres,
        meta: {
          titleKey: 'appointmentTyres',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'appointmentTyres',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/history-interventions/:interventionType',
        component: HistoryInterventions,
        meta: {
          titleKey: 'historyOfInterventions',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'historyInterventions',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/my-account/change-password',
        component: ChangePassword,
        meta: {
          titleKey: 'changePassword',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'changePassword',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/my-account/personal-information',
        component: PersonalInformation,
        meta: {
          titleKey: 'personalInformation',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'personalInformation',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/my-account/vehicle-information',
        component: VehicleInformation,
        meta: {
          titleKey: 'vehicleInformation',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'vehicleInformation',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/other-services/card-fuel',
        component: CardFuel,
        meta: {
          titleKey: 'cardFuel',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'cardFuel',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/other-services/return-car',
        component: ReturnCar,
        meta: {
          titleKey: 'returnCar',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'returnCar',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/other-services/documentation',
        component: Documentation,
        meta: {
          titleKey: 'documentation',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'documentation',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_DOCUMENTATION_MASTERDATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/other-services/request-replacement-vehicle',
        component: RequestReplacementVehicle,
        meta: {
          titleKey: 'requestReplacementVehicle',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'requestReplacementVehicle',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      // TODO: Deshabilitado hasta proximo aviso
      // {
      //   path: '/other-services/fines',
      //   component: Fines,
      //   meta: {
      //     titleKey: 'fines',
      //     requiresAuth: true,
      //     roles: [APP_ROLES.DRIVER_ROLE]
      //   },
      //   name: 'fines',
      //   async beforeEnter(to, from, next) {
      //     const driverId = userDriverId();
      //     const vehicles = store.getters['vehicleInfo/data']('vehicles');
      //     const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
      //     if (!vehicles.length) {
      //       promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
      //     }
      //     Promise.all(promises)
      //       .then(() => next())
      //       .catch(() => {
      //         redirectToErrorPage(next, '');
      //       });
      //   }
      // },
      // TODO: Deshabilitado hasta proximo aviso
      // {
      //   path: '/other-services/incidents-DGT',
      //   component: IncidentsDGT,
      //   meta: {
      //     titleKey: 'incidentsDGT',
      //     requiresAuth: true,
      //     roles: [APP_ROLES.DRIVER_ROLE]
      //   },
      //   name: 'incidentsDGT',
      //   async beforeEnter(to, from, next) {
      //     const driverId = userDriverId();
      //     const vehicles = store.getters['vehicleInfo/data']('vehicles');
      //     const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
      //     if (!vehicles.length) {
      //       promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
      //     }
      //     Promise.all(promises)
      //       .then(() => next())
      //       .catch(() => {
      //         redirectToErrorPage(next, '');
      //       });
      //   }
      // },
      // TODO: Deshabilitado hasta proximo aviso
      // {
      //   path: '/other-services/points-interest',
      //   component: PointsInterest,
      //   meta: {
      //     titleKey: 'pointsInterest',
      //     requiresAuth: true,
      //     roles: [APP_ROLES.DRIVER_ROLE]
      //   },
      //   name: 'pointsInterest',
      //   async beforeEnter(to, from, next) {
      //     const driverId = userDriverId();
      //     const vehicles = store.getters['vehicleInfo/data']('vehicles');
      //     const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
      //     if (!vehicles.length) {
      //       promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
      //     }
      //     Promise.all(promises)
      //       .then(() => next())
      //       .catch(() => {
      //         redirectToErrorPage(next, '');
      //       });
      //   }
      // },
      {
        path: '/roadside-assistance',
        component: RoadsideAssistance,
        meta: {
          titleKey: 'roadsideAssistance',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'roadsideAssistance',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/roadside-assistance/accident-management',
        component: AccidentManagement,
        meta: {
          titleKey: 'accidentManagement',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'accidentManagement',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/roadside-assistance/contact-assistance',
        component: ContactAssistance,
        meta: {
          titleKey: 'contactAssistance',
          requiresAuth: true,
          roles: [APP_ROLES.DRIVER_ROLE]
        },
        name: 'contactAssistance',
        async beforeEnter(to, from, next) {
          const driverId = userDriverId();
          const vehicles = store.getters['vehicleInfo/data']('vehicles');
          const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
          if (!vehicles.length) {
            promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
          }
          Promise.all(promises)
            .then(() => next())
            .catch(() => {
              redirectToErrorPage(next, '');
            });
        }
      },
      {
        path: '/remember-password',
        component: Remember,
        meta: { titleKey: 'remember', requiresAuth: false },
        name: 'remember',
        async beforeEnter(to, from, next) {
          const signedIn = userSignedIn();
          if (!signedIn) return next();
          next({
            name: 'dashboard',
            query: to.query
          });
        }
      },
      {
        path: '/remember-password/verify-pass',
        component: VerifyPass,
        meta: {
          titleKey: 'verifyPass',
          requiresAuth: false
        },
        name: 'verifyPass',
        async beforeEnter(to, from, next) {
          const signedIn = userSignedIn();
          if (!signedIn) return next();
          next({
            name: 'dashboard',
            query: to.query
          });
        }
      },
      {
        path: '/new-user',
        component: NewUser,
        meta: { titleKey: 'newUser', requiresAuth: false },
        name: 'newUser',
        async beforeEnter(to, from, next) {
          const signedIn = userSignedIn();
          if (!signedIn) return next();
          next({
            name: 'dashboard',
            query: to.query
          });
        }
      },
      {
        path: '/new-user/confirm-data',
        component: ConfirmData,
        meta: {
          titleKey: 'confirmData',
          requiresAuth: false
        },
        name: 'confirmData',
        async beforeEnter(to, from, next) {
          const signedIn = userSignedIn();

          store
            .dispatch(`vehicleInfo/${OFFER_LEGAL_CONDITIONS}`, 'confirm-user')
            .then(() => {
              if (!signedIn) return next();
              next({
                name: 'dashboard',
                query: to.query
              });
            })
            .catch(() => {});
        }
      },
      // TODO: Deshabilitado hasta proximo aviso
      // {
      //   path: '/notifications',
      //   component: Notifications,
      //   meta: {
      //     titleKey: 'notifications',
      //     requiresAuth: true,
      //     roles: [APP_ROLES.DRIVER_ROLE]
      //   },
      //   name: 'notifications',
      //   async beforeEnter(to, from, next) {
      //     const driverId = userDriverId();
      //     const vehicles = store.getters['vehicleInfo/data']('vehicles');
      //     const promises = [store.dispatch(`ui/${UI_GET_MASTER_DATA}`)];
      //     if (!vehicles.length) {
      //       promises.push(store.dispatch(`vehicleInfo/${GET_DRIVER}`, driverId));
      //     }
      //     Promise.all(promises)
      //       .then(() => next())
      //       .catch(() => {
      //         redirectToErrorPage(next, '');
      //       });
      //   }
      // },
      {
        path: 'legal/:type',
        component: Legal,
        name: 'legal',
        props: { whithoutLinksFooter: true },
        meta: { titleKey: 'legal', requiresAuth: false }
      },
      {
        path: 'error',
        name: 'error',
        component: Error,
        props: { type: '500_es-ES' },
        meta: { titleKey: 'error', requiresAuth: false }
      },
      // {
      //   path: 'access-denied',
      //   name: 'access-denied',
      //   component: PageAccessDenied,
      //   meta: {
      //     titleKey: 'accessDenied',
      //     requiresAuth: false
      //   }
      // },
      {
        path: '*',
        name: '404',
        component: Error,
        props: { type: '404_es-ES' },
        meta: { titleKey: 'notFound', requiresAuth: false },
        async beforeEnter(to, from, next) {
          if (to.path === '/') {
            const signedIn = userSignedIn();
            next({
              name: signedIn ? 'dashboard' : 'login',
              query: to.query
            });
          } else {
            next();
          }
        }
      }
    ]
  }
];
